import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import ModalDialogContext from "../../../../../../../Context/ModalDialog/ModalDialogContext";
import {
  NoAccessScreen,
  LoadingIndicator,
  LongText,
} from "../../../../../../../Component";
import { styles } from "./styles";
import {
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  withStyles,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import { getDataAugmentation } from "../../../../../../../API/Entity";
import CreateAddress from "../../../../../../EntityAddresses/Components/CreateAddress";
import { recordTypes } from "../../../../../../../API/config";

const AddressesTable = (props) => {
  const {
    entity,
    sources,
    availableSources,
    reloadParent,
    classes,
    hasRecordPermission,
  } = props;

  const { showErrorMessage } = useContext(ModalDialogContext);
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [sourceNames, setSourceNames] = useState([]);
  const [augmentableAddresses, setAugmentableAddresses] = useState({});
  const [addressMaxRows, setAddressMaxRows] = useState(0);
  const [openAddAddressDialog, setOpenAddAddressDialog] = useState(false);
  const [currentAddress, setCurrentAddress] = useState({});
  const [hasAccess, setHasAccess] = useState(false);

  const resetData = () => {
    setSourceNames([]);
    setAugmentableAddresses({});
    setAddressMaxRows(0);
    setCurrentAddress({});
  };

  useEffect(() => {
    const setAugmentationData = (augmentationData) => {
      const augmentableAddresses = {};
      let addressMaxRows = 0;
      Object.keys(augmentationData).forEach((sourceName) => {
        const { addresses = [] } = augmentationData[sourceName];
        augmentableAddresses[sourceName] = addresses;
        addressMaxRows = Math.max(addressMaxRows, addresses.length);
        setSourceNames((oldVal) => [...oldVal, sourceName]);
      });
      setAugmentableAddresses(augmentableAddresses);
      setAddressMaxRows(addressMaxRows);
      setLoading(false);
    };

    const loadAvailableAddresses = async () => {
      setLoading(true);
      try {
        const response = await getDataAugmentation(
          entity.id,
          availableSources
            .filter((s) => sources.map(({ label }) => label).includes(s.label))
            .map((s) => s.value),
          "addresses",
        );
        const { data = {} } = response;
        const { sourcesData = {}, errors } = data || {};
        if (errors && errors.length > 0) {
          throw new Error(errors.join("\n"));
        }
        setAugmentationData(sourcesData);
      } catch (error) {
        showErrorMessage({ error });
      }
    };

    const setPermissions = async () => {
      const { permissions = {} } = entity;

      const canAddAddress = hasRecordPermission(
        permissions,
        recordTypes.record.actions.addAddress.name,
      );
      setHasAccess(canAddAddress);

      if (canAddAddress) {
        await loadAvailableAddresses();
      }
    };

    if (sources) {
      resetData();
      setPermissions();
    }
  }, [entity, sources]);

  const addAddress = (address) => {
    setCurrentAddress(address);
    setOpenAddAddressDialog(true);
  };

  const closeAddAddressDialog = (status) => {
    setOpenAddAddressDialog(false);
    if (status != "cancel") {
      reloadParent(true);
    }
  };

  const renderAddressTableHeader = () => {
    return (
      <TableHead>
        <TableRow>
          {sourceNames.map((sourceName, idx) => (
            <TableCell
              key={idx}
              scope="col"
              align="center"
              className={
                idx === 0 ? classes.columnHeader : classes.contentDividerHeader
              }
            >
              <LongText
                text={sourceName}
                width={sourceNames.length > 2 ? "200px" : "100%"}
              />
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  };

  const renderAddressTableBody = () => (
    <TableBody>
      {[...Array(addressMaxRows).keys()].map((idx) => (
        <TableRow key={idx}>
          {sourceNames.map((source, i) => {
            return (
              <TableCell
                key={i}
                scope="col"
                className={
                  i === 0 ? classes.fieldValue : classes.contentDividerCell
                }
              >
                {augmentableAddresses[source][idx] ? (
                  <Grid container={true}>
                    <Grid item={true} xs={10}>
                      <Typography component="p" variant={"body1"}>
                        {augmentableAddresses[source][idx].address_line_1}
                      </Typography>
                      <Typography component="p" variant={"body1"}>
                        {augmentableAddresses[source][idx].city +
                          " - " +
                          augmentableAddresses[source][idx].postcode}
                      </Typography>
                      <Typography component="p" variant={"caption"}>
                        {augmentableAddresses[source][idx].countryName}
                      </Typography>
                    </Grid>
                    <Grid
                      item={true}
                      xs={2}
                      style={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      <Button
                        disableRipple
                        disableElevation
                        aria-label="add address"
                        onClick={() => {
                          addAddress(augmentableAddresses[source][idx]);
                        }}
                      >
                        <AddIcon />
                      </Button>
                    </Grid>
                  </Grid>
                ) : (
                  ""
                )}
              </TableCell>
            );
          })}
        </TableRow>
      ))}
    </TableBody>
  );

  const renderLoadingIndicator = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "30vh",
        }}
      >
        <LoadingIndicator />
      </div>
    );
  };

  const renderAddressTable = () => {
    return (
      <>
        {openAddAddressDialog && (
          <CreateAddress
            open={openAddAddressDialog}
            onclose={closeAddAddressDialog}
            newContact={true}
            editContact={false}
            address={currentAddress}
            entity={entity}
            action={recordTypes.record.actions.augmentData.name}
          />
        )}
        <Grid container>
          <TableContainer>
            <Table className={classes.table} size="small">
              {renderAddressTableHeader()}
              {renderAddressTableBody()}
            </Table>
          </TableContainer>
        </Grid>
      </>
    );
  };

  return hasAccess ? (
    loading ? (
      renderLoadingIndicator()
    ) : (
      renderAddressTable()
    )
  ) : (
    <div style={{ position: "relative" }}>
      <NoAccessScreen />
    </div>
  );
};

export default withStyles(styles, { withTheme: true })(AddressesTable);
